import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';
import { environment } from '../../environments/environment';

// HeyCentric book-after-payment (pre-basket) client. Mirrors the Westminster
// ceremonies service; the only deliberate divergence is the environment-aware
// base URL below (Westminster hardcodes staging, which is the known
// develop→main URL trap).
@Injectable({
  providedIn: "root",
})
export class HeyCentricService {

  private url = environment.production
    ? 'https://api.bookinglab.co.uk'
    : 'https://staging.bookinglab.co.uk';
  private path = "/";
  heyCentricUrlPath = "/payment/";

  constructor(private http: HttpClient) {}

  // Normalise a pre-basket failure into one shape the summary component can
  // branch on. The backend's pre-payment gate answers 409 with
  // [{err: "Payment blocked before charge: <reason>"}] — `blocked: true` means
  // the basket itself is unusable (empty / not on this session), so replaying
  // the same body is futile and the caller should rebuild the basket instead.
  private toPaymentError(err: any, body: any) {
    if (err.requestBody) {
      return err;
    }
    const blocked = err.status === 409;
    const reason = (blocked && Array.isArray(err.error) && err.error[0] && err.error[0].err)
      ? err.error[0].err
      : undefined;
    return { status: err.status, message: reason || err.message, blocked, reason, requestBody: body };
  }

  async preBasketInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, parentCompanyId?) {
    let port = '';
    const ports:any = ['80', '443'];
    console.log('port', window.location.port);
    if(ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    const body = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT company (e.g. 37002). child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: 'ceremony'
    }

    if (Array.isArray(revCode)) {
      body['rev_codes'] = revCode;
    } else {
      body['rev_code'] = `${revCode}`;
    }

    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    } else {
      body['amount'] = amounts;
    }

    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }

  async preBasketInvokeRetry(body: any) {
    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }

  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/find`, body).toPromise();
  }

  // Kept for parity with the server contract; the live journey does NOT call
  // this. Writing RETURNED_FROM_GATEWAY from the client can regress a record
  // the webhook has already advanced to PAID, so the verify page only polls.
  async preBasketUpdate(companyId, basketId, serviceItemId, status,ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/update`, body).toPromise();
  }

  async preBasketTelephonyInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, bu, agentId, agentEmail, agentPhone, parentCompanyId?) {
    let port = '';
    const ports:any = ['80', '443'];
    console.log('port', window.location.port);
    if(ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    const body = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT company (e.g. 37002). child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      bu: `${bu}`,
      "agent_id": agentId,
      "agent_email": `${agentEmail}`,
      "agent_phone": `${agentPhone}`
    }

    if (Array.isArray(revCode)) {
      body['rev_codes'] = revCode;
    } else {
      body['rev_code'] = `${revCode}`;
    }

    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    } else {
      body['amount'] = amounts;
    }

    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/arp`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }

  async preBasketTelephonyInvokeRetry(body: any) {
    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/arp`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }
}
